// frontend/packages/business-console/src/components/build-interview/SitePreview.jsx
//
// Live recolouring homepage preview for the Look & feel step (Website Builder
// Redesign, Phase 2). A representative mini homepage (nav + gradient hero + 3
// service cards) that re-skins instantly as the customer picks brand colours /
// logo / tone. Generic-but-faithful for v1 (the handoff signed off on this):
// the layout is fixed, but the colours + business name + services come from the
// customer's own answers.
//
// Recolouring is deterministic + client-side (no generation pipeline): the
// chosen primary becomes the dark brand surface (nav + hero gradient), with a
// readable text colour picked by luminance. No backend.

import React from 'react';
import { Box, Typography } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import useTokens from '../primitives/useTokens';

const clampByte = (n) => Math.max(0, Math.min(255, Math.round(n)));
function parseHex(input) {
  let h = String(input || '').replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function toHex({ r, g, b }) {
  return (
    '#' +
    [r, g, b].map((x) => clampByte(x).toString(16).padStart(2, '0')).join('')
  );
}
function darken(hex, amt) {
  const c = parseHex(hex);
  if (!c) return hex;
  return toHex({ r: c.r * (1 - amt), g: c.g * (1 - amt), b: c.b * (1 - amt) });
}
function readableOn(hex) {
  const c = parseHex(hex);
  if (!c) return '#ffffff';
  const lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return lum > 0.62 ? '#0b1411' : '#ffffff';
}

const TONE_HEADLINE = {
  professional: 'Trusted service, done right the first time.',
  friendly: 'Friendly local pros you can count on.',
  premium: 'Comfort you can count on, in every season.',
  straight_talking: 'Honest work. Fair prices. No surprises.',
};

export default function SitePreview({
  colors = [],
  tone = '',
  logoUrl = null,
  businessName = 'Your business',
  industry = '',
  services = [],
  areas = [],
}) {
  const t = useTokens();
  const primary = parseHex(colors[0]) ? colors[0] : '#1e293b';
  const heroBg = `linear-gradient(160deg, ${primary} 0%, ${darken(primary, 0.28)} 60%, ${darken(primary, 0.12)} 100%)`;
  const onPrimary = readableOn(primary);
  const onPrimaryMuted =
    onPrimary === '#ffffff' ? 'rgba(255,255,255,.78)' : 'rgba(11,20,17,.7)';

  const headline = TONE_HEADLINE[tone] || 'Comfort you can count on.';
  const eyebrow = (industry || 'Local service').toUpperCase();
  const svc = (
    services.length
      ? services
      : [
          { name: 'Service one' },
          { name: 'Service two' },
          { name: 'Service three' },
        ]
  ).slice(0, 3);
  const domain =
    `${businessName.toLowerCase().replace(/[^a-z0-9]+/g, '')}.com`.slice(0, 28);
  const areaLine = areas.length ? areas.slice(0, 2).join(', ') : 'your area';

  return (
    <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'flex-start' }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: t.green,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Box
          data-bi-anim
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: t.green,
            animation: 'bi-softpulse 1.4s ease-in-out infinite',
          }}
        />
        Live preview
        <Box
          component="span"
          sx={{
            color: t.faint,
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: 0,
          }}
        >
          updates as you choose
        </Box>
      </Typography>

      <Box
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          border: `1px solid ${t.cardBorder}`,
          boxShadow: t.cardShadow,
          background: t.card,
        }}
      >
        {/* browser chrome */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 1,
            background: t.subtle,
            borderBottom: `1px solid ${t.hairline}`,
          }}
        >
          {['#f87171', '#fbbf24', '#34d399'].map((c) => (
            <Box
              key={c}
              sx={{ width: 9, height: 9, borderRadius: '50%', background: c }}
            />
          ))}
          <Typography sx={{ fontSize: 11, color: t.faint, ml: 1 }}>
            {domain}
          </Typography>
        </Box>

        {/* nav + hero (recolours) */}
        <Box
          sx={{ background: heroBg, color: onPrimary, px: 2.5, pt: 2, pb: 3 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '7px',
                  background: 'rgba(255,255,255,.16)',
                  display: 'grid',
                  placeItems: 'center',
                  overflow: 'hidden',
                }}
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <ArrowOutwardIcon sx={{ fontSize: 15, color: onPrimary }} />
                )}
              </Box>
              <Typography
                sx={{ fontSize: 14, fontWeight: 800, color: onPrimary }}
              >
                {businessName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {['Services', 'Areas', 'About'].map((l) => (
                <Typography
                  key={l}
                  sx={{
                    fontSize: 11.5,
                    color: onPrimaryMuted,
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {l}
                </Typography>
              ))}
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: primary,
                  background: '#fff',
                  borderRadius: '7px',
                  px: 1.25,
                  py: 0.5,
                }}
              >
                Call now
              </Box>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '.08em',
              color: onPrimaryMuted,
              mb: 0.75,
            }}
          >
            {eyebrow} {areas.length ? `· ${areaLine}` : ''}
          </Typography>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              letterSpacing: '-0.5px',
              lineHeight: 1.1,
              mb: 1,
              color: onPrimary,
            }}
          >
            {headline}
          </Typography>
          <Typography
            sx={{ fontSize: 12.5, color: onPrimaryMuted, mb: 2, maxWidth: 360 }}
          >
            {/* "Experts in X" reads correctly for ANY industry phrase; the old
                "X experts" broke on plurals ("exotic animals experts"). */}
            {industry ? `Experts in ${industry}` : 'Local experts'}, serving{' '}
            {areaLine}
            {areas.length ? ' and nearby.' : '.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: primary,
                background: '#fff',
                borderRadius: '8px',
                px: 1.75,
                py: 0.75,
              }}
            >
              Book a visit
            </Box>
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: onPrimary,
                border: `1px solid ${onPrimaryMuted}`,
                borderRadius: '8px',
                px: 1.75,
                py: 0.75,
              }}
            >
              Call now
            </Box>
          </Box>
        </Box>

        {/* services row */}
        <Box sx={{ px: 2.5, py: 2, background: t.card }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '.06em',
              color: t.faint,
              mb: 1.25,
            }}
          >
            WHAT WE DO
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {svc.map((s, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: `1px solid ${t.hairline}`,
                  borderRadius: '10px',
                  p: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '5px',
                    background: primary,
                    mb: 1,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: t.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.name}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: t.faint }}>
                  Learn more
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: 11,
          color: t.faint,
          mt: 1.5,
          textAlign: 'center',
          maxWidth: 380,
          mx: 'auto',
        }}
      >
        This is a real draft, not a stock template. Colours, copy and sections
        come straight from your answers.
      </Typography>
    </Box>
  );
}
